/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { ExpandCaret } from '@/Client/Icons';
import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';
import { type ScanPhotoTimelineRow } from '@kaiizen/kai-scan-template-system';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { useProgressAlt } from './progressAltContext';
import {
  COLLAPSED_ROW_HEIGHT_PX,
  CONTROL_HEIGHT_PX,
  GAP_PX,
  HEADER_HEIGHT_PX,
  ROW_LABEL_PADDING_LEFT_PX,
  ROW_LABEL_PADDING_RIGHT_PX,
  ROW_LABEL_PADDING_TOP_PX,
} from './progressAltMetrics';
import { ProgressAltPeriodPicker } from './ProgressAltPeriodPicker';
import {
  getFirstPeriodStart,
  getRowLabel,
  getRowNumberLabel,
  getRowPhotoCountLabel,
} from './progressAltRowLabelLib';
import { FOCUS_RING_STYLE } from './progressAltStyles';

/**
 * The dates ride in their own column rather than as individually-pinned labels, so the whole
 * column — background, divider and all — holds still while the photo grid scrolls under it.
 */
const COLUMN_STYLE = css`
  label: PROGRESS_ALT_DATE_COLUMN_STYLE;
  position: sticky;
  left: 0;
  z-index: 2;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${GAP_PX}px;
  border-right: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
`;

/**
 * The period picker sits here rather than out in the tab bar because this column is what it decides
 * the wording of. Its controls are centred in the cell rather than pushed around by padding, so they
 * stay put whichever shape the picker is wearing and however wide the column is.
 */
const CORNER_STYLE = css`
  label: PROGRESS_ALT_DATE_COLUMN_CORNER_STYLE;
  position: sticky;
  top: 0;
  z-index: 1;
  box-sizing: border-box;
  flex: 0 0 ${HEADER_HEIGHT_PX}px;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-top: 10px;
  background: ${COLORS.sunken};
`;

/** Wears the header bars' expand affordance, since collapsing a column is the same act either way. */
const COLLAPSE_BUTTON_STYLE = css`
  box-sizing: border-box;
  flex: 0 0 auto;
  width: ${CONTROL_HEIGHT_PX}px;
  height: ${CONTROL_HEIGHT_PX}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.strokeFaded};
  border-radius: 0;
  background: ${COLORS.white};
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.midtone};
  }

  ${FOCUS_RING_STYLE}
`;

/**
 * The whole label is the row's expand/collapse target, so it spans the column edge to edge and
 * lights up as one block on hover — the hit area should be obvious, not implied by the text.
 */
const LABEL_STYLE = css`
  label: PROGRESS_ALT_ROW_LABEL_STYLE;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 6px;
  padding: ${ROW_LABEL_PADDING_TOP_PX}px ${ROW_LABEL_PADDING_RIGHT_PX}px 0 ${ROW_LABEL_PADDING_LEFT_PX}px;
  border: 0;
  background: transparent;
  color: ${COLORS.ink};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${COLORS.appBg};
  }

  ${FOCUS_RING_STYLE}
`;

const LABEL_TEXT_STYLE = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DATE_STYLE = css`
  font-family: ${FONT.display};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${pxToRem(13)};
  font-weight: 600;
  letter-spacing: 0.01em;
`;

const DETAIL_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

const CARET_STYLE = css`
  flex: 0 0 auto;
  display: inline-flex;
  transition: transform 120ms ease;
`;

const ProgressAltRowLabel = ({ row, firstPeriodStart }: {
  row: ScanPhotoTimelineRow;
  firstPeriodStart: number;
}) => {
  const {
    timeline,
    tileSize,
    dateColumnCollapsed,
    getRowNumber,
    collapsedRows,
    toggleCollapsedRow,
  } = useProgressAlt();
  const period = timeline!.period;
  const rowNumber = getRowNumber(row);
  const collapsed = collapsedRows.has(rowNumber);
  const dateLabel = getRowLabel(row, period, firstPeriodStart);
  const numberLabel = getRowNumberLabel(row, period, firstPeriodStart);
  // The whole column being shut leaves room for the row's number and nothing more.
  const columnCollapsed = dateColumnCollapsed;
  const onToggleCollapsed = () => toggleCollapsedRow(rowNumber);
  return (
    <button
      type='button'
      css={LABEL_STYLE}
      style={{ flex: `0 0 ${collapsed ? COLLAPSED_ROW_HEIGHT_PX : tileSize.tileHeightPx}px` }}
      data-testid='progress-alt-row-label'
      // The date the number stands for, for whoever wants it back without reopening the column.
      title={columnCollapsed ? dateLabel : undefined}
      aria-label={`${collapsed ? 'Expand' : 'Collapse'} row ${dateLabel}`}
      aria-expanded={!collapsed}
      onClick={onToggleCollapsed}
    >
      <span css={LABEL_TEXT_STYLE}>
        <span css={DATE_STYLE} data-testid='progress-alt-row-date'>
          {columnCollapsed ? numberLabel : dateLabel}
        </span>
        {/* No room for the count in a row shut down to a strip, nor in a column shut to its reckoning. */}
        {collapsed || columnCollapsed ? null : (
          <span css={DETAIL_STYLE} data-testid='progress-alt-row-photo-count'>
            {getRowPhotoCountLabel(row)}
          </span>
        )}
      </span>
      {
        /* Points the way the row will move: down to open it out, up to shut it back. Trails the label
          rather than leading it, so the dates stay flush with the left edge they are read down. Wears
          the column bars' caret colour, this being the same affordance on the other axis. */
      }
      <span css={CARET_STYLE} style={{ transform: `rotate(${collapsed ? 90 : -90}deg)` }}>
        <ExpandCaret color={COLORS.midtone} />
      </span>
    </button>
  );
};

/**
 * The pinned left column: the period picker above one clickable date label per row, matching the
 * grid's row heights.
 *
 * Collapsed it drops the prose and keeps the reckoning: the picker shrinks to the name of the period
 * in force and each row shows just how far into the scan it sits. That hands most of the column's
 * width back to the photos, and anything it no longer has room for is a click on the way back out.
 */
export const ProgressAltDateColumn = ({ rows, widthPx }: {
  rows: readonly ScanPhotoTimelineRow[];
  widthPx: number;
}) => {
  const { dateColumnCollapsed, toggleDateColumnCollapsed, getRowNumber } = useProgressAlt();
  const firstPeriodStart = getFirstPeriodStart(rows);
  return (
    <div
      css={COLUMN_STYLE}
      style={{ flex: `0 0 ${widthPx}px` }}
      data-testid='progress-alt-date-column'
      data-progress-alt-collapsed={dateColumnCollapsed ? 'true' : undefined}
    >
      <div css={CORNER_STYLE}>
        <ProgressAltPeriodPicker />
        {/* Nothing to shut once shut, and the chip beside it already carries the way back out. */}
        {dateColumnCollapsed ? null : (
          <button
            type='button'
            css={COLLAPSE_BUTTON_STYLE}
            data-testid='progress-alt-date-column-toggle'
            aria-label='Collapse date column'
            aria-expanded
            onClick={toggleDateColumnCollapsed}
          >
            {/* Points left, at the width the column is about to give up. */}
            <ExpandCaret expanded color={COLORS.midtone} />
          </button>
        )}
      </div>
      {rows.map(row => (
        <ProgressAltRowLabel
          key={`label:${getRowNumber(row)}`}
          row={row}
          firstPeriodStart={firstPeriodStart}
        />
      ))}
    </div>
  );
};
